import { ReactElement, ReactNode, useState } from 'react';

import { Collapse } from '@mui/material';

import { Divider, Icons, LinkBehaviour, Stack } from '@cast/design-system';

import { VerticalSettingsForm } from '../layout/VerticalSettingsForm';
import { DynamicThresholdSimulation } from './_components/DynamicThresholdSimulation';
import { FormSection } from './components/FormSection';
import { vpaSettingsConstants } from '../../_constants/settingsConstants';

const AdvancedSettings = ({ children }: { children: ReactNode }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Stack data-testid="advanced-settings" mt={40}>
      <LinkBehaviour
        variant="P12M"
        display="flex"
        alignItems="center"
        gap={4}
        mb={16}
        onClick={() => setIsExpanded(!isExpanded)}
        testId="advanced-settings-expander"
      >
        Advanced settings
        {isExpanded ? (
          <Icons.CaretUp size={16} />
        ) : (
          <Icons.CaretDown size={16} />
        )}
      </LinkBehaviour>
      <Collapse in={isExpanded}>{children}</Collapse>
    </Stack>
  );
};

type Props = {
  isAutoscalerModeImmediately: boolean;
  autoscalerModeField: ReactElement;
  rolloutBehaviorField: ReactElement;
  antiAffinityField: ReactElement;
  optimizationThresholdField: ReactElement;
  downscalingModeField: ReactElement;
  memoryEventField: ReactElement;

  renderAntiAffinityFormSection?: (field: ReactElement) => ReactElement;
};
export const AutoscalerSettingsContainer = ({
  isAutoscalerModeImmediately,
  rolloutBehaviorField,
  antiAffinityField,
  autoscalerModeField,
  optimizationThresholdField,
  downscalingModeField,
  memoryEventField,

  renderAntiAffinityFormSection,
}: Props) => {
  const {
    title,
    description,
    docsLinkKey,
    downscaling,
    memoryEvent,
    threshold,
    rolloutBehaviour,
    antiAffinity,
  } = vpaSettingsConstants.autoscaler;

  return (
    <VerticalSettingsForm
      title={title}
      description={description}
      docsLinkKey={docsLinkKey}
    >
      <Stack>
        {autoscalerModeField}
        <AdvancedSettings>
          <Stack gap={16}>
            {isAutoscalerModeImmediately ? (
              <>
                <FormSection
                  title={threshold.title}
                  description={threshold.description}
                >
                  {optimizationThresholdField}
                  <DynamicThresholdSimulation />
                </FormSection>

                <Divider />

                <FormSection
                  title={rolloutBehaviour.title}
                  description={rolloutBehaviour.description}
                >
                  {rolloutBehaviorField}
                </FormSection>

                <Divider />

                <FormSection
                  title={downscaling.title}
                  description={downscaling.description}
                >
                  {downscalingModeField}
                </FormSection>

                <Divider />

                {renderAntiAffinityFormSection ? (
                  renderAntiAffinityFormSection(antiAffinityField)
                ) : (
                  <FormSection
                    title={antiAffinity.title}
                    description={antiAffinity.description}
                  >
                    {antiAffinityField}
                  </FormSection>
                )}
              </>
            ) : (
              <FormSection
                title={memoryEvent.title}
                description={memoryEvent.description}
              >
                {memoryEventField}
              </FormSection>
            )}
          </Stack>
        </AdvancedSettings>
      </Stack>
    </VerticalSettingsForm>
  );
};
